import type { RefObject } from 'react'
import { useLayoutEffect, useRef, useState } from 'react'

// Space between badges in px; matches gap="2"
const BADGE_GAP_PX = 8
// Row counts as resizing until it has been still this long
const RESIZE_SETTLE_MS = 150

type UseOverflowRestraintResult = {
  containerRef: RefObject<HTMLDivElement | null>
  itemRefs: RefObject<(HTMLElement | null)[]>
  moreBadgeRefs: RefObject<(HTMLElement | null)[]>
  trailingRef: RefObject<HTMLDivElement | null>
  visibleCount: number
  isResizing: boolean
}

// How many badges fit on one line, leaving room for "+N more" when some don't
const fitCount = (
  itemWidths: number[],
  moreWidths: number[],
  reserved: number,
  available: number,
): number => {
  const count = itemWidths.length
  // A row with no width is hidden or not laid out yet; collapsing everything would flash "+N more"
  if (available <= 0) return count

  let used =
    itemWidths.reduce((sum, width) => sum + width, 0) +
    BADGE_GAP_PX * Math.max(0, count - 1)
  if (used + reserved <= available) return count

  for (let fit = count - 1; fit > 0; fit--) {
    used -= itemWidths[fit]! + BADGE_GAP_PX
    // Button width depends on the digit count of the N it would show
    const moreWidth = moreWidths[String(count - fit).length - 1] ?? 0
    if (used + BADGE_GAP_PX + moreWidth + reserved <= available) return fit
  }
  return 0
}

const widthOf = (el: HTMLElement | null): number =>
  el?.getBoundingClientRect().width ?? 0

// Measures a hidden copy of the badges to work out how many fit on one line
export const useOverflowRestraint = (
  itemCount: number,
  filterValuesKey: string,
  hasTrailing: boolean,
): UseOverflowRestraintResult => {
  const containerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLElement | null)[]>([])
  // Indexed by digit count - 1
  const moreBadgeRefs = useRef<(HTMLElement | null)[]>([])
  const trailingRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(itemCount)
  const [isResizing, setIsResizing] = useState(false)

  useLayoutEffect(() => {
    // The container must be a direct child of the scrolling row, or the budget collapses to zero
    const row = containerRef.current?.parentElement
    if (!row) return

    // Read live: badge text size changes at the sm breakpoint, so cached widths go stale
    const recompute = (): void => {
      const trailingWidth = trailingRef.current
        ? widthOf(trailingRef.current) + BADGE_GAP_PX
        : 0
      setVisibleCount(
        fitCount(
          itemRefs.current.slice(0, itemCount).map(widthOf),
          moreBadgeRefs.current.map(widthOf),
          trailingWidth,
          widthOf(row),
        ),
      )
    }
    recompute()

    let timeout: ReturnType<typeof setTimeout> | undefined
    let lastWidth: number | undefined
    // Recomputed on every resize so badges follow the row through the sidebar slide; setState bails on an unchanged count
    const observer = new ResizeObserver(() => {
      recompute()

      // The first callback is the initial observation and Reset resizes leave the row alone, so neither dims
      const width = widthOf(row)
      if (lastWidth !== undefined && width !== lastWidth) {
        setIsResizing(true)
        clearTimeout(timeout)
        timeout = setTimeout(() => setIsResizing(false), RESIZE_SETTLE_MS)
      }
      lastWidth = width
    })
    observer.observe(row)
    if (trailingRef.current) observer.observe(trailingRef.current)

    return () => {
      clearTimeout(timeout)
      // The cleared timer would otherwise leave the row dimmed until the next resize
      setIsResizing(false)
      observer.disconnect()
    }
  }, [itemCount, filterValuesKey, hasTrailing])

  return {
    containerRef,
    itemRefs,
    moreBadgeRefs,
    trailingRef,
    visibleCount,
    isResizing,
  }
}
